/**
 * Anwenden der Filter auf die Rezeptliste.
 *
 * Reine Funktionen ohne Zustand, damit sie einzeln getestet werden können.
 */
import type { RecipeSummary } from '../api/types.ts';
import { normalizeText } from './search.ts';
import type { RecipeFilter, SortKey } from './search.ts';

/** Findet die Rezepte, die zum Suchtext passen. */
export function matchByQuery(
  recipes: readonly RecipeSummary[],
  query: string,
  index: MiniSearchLike | null,
): RecipeSummary[] {
  const trimmed = query.trim();
  if (trimmed === '' || index === null) {
    return [...recipes];
  }

  const hits = index.search(trimmed);
  const order = new Map(hits.map((hit, position) => [String(hit.id), position]));
  return recipes
    .filter((recipe) => order.has(recipe.id))
    .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}

/** Kleine Schnittstelle, damit die Funktion testbar bleibt. */
export interface MiniSearchLike {
  search(query: string): { id: unknown }[];
}

/** Ein Rezept passt, wenn alle gesetzten Bedingungen zutreffen. */
export function matchesFilter(
  recipe: RecipeSummary,
  filter: RecipeFilter,
  ingredientNames: readonly string[] | null,
): boolean {
  if (filter.categoryIds.length > 0) {
    const hasCategory = filter.categoryIds.some((id) => recipe.categoryIds.includes(id));
    if (!hasCategory) {
      return false;
    }
  }

  if (filter.tagNames.length > 0) {
    const wanted = filter.tagNames.map((name) => normalizeText(name));
    const present = recipe.tagNames.map((name) => normalizeText(name));
    const ok = filter.tagsAll
      ? wanted.every((name) => present.includes(name))
      : wanted.some((name) => present.includes(name));
    if (!ok) {
      return false;
    }
  }

  if (filter.difficulties.length > 0 && !filter.difficulties.includes(recipe.difficulty)) {
    return false;
  }

  if (filter.maxTotalMinutes > 0 && recipe.totalMinutes > filter.maxTotalMinutes) {
    return false;
  }

  if (filter.maxActiveMinutes > 0 && recipe.activeMinutes > filter.maxActiveMinutes) {
    return false;
  }

  if (filter.unratedOnly) {
    if (recipe.ratingCount > 0) {
      return false;
    }
  } else if (filter.minRating > 0) {
    const value = filter.ratingByMe ? recipe.myStars : recipe.ratingAverage;
    if (value === null || value < filter.minRating) {
      return false;
    }
  }

  if (filter.favoritesOnly && !recipe.isFavorite) {
    return false;
  }

  if (filter.cookedBefore !== null) {
    // Noch nie gekocht zählt ebenfalls als "lange her".
    if (recipe.lastCookedAt !== null && recipe.lastCookedAt > filter.cookedBefore) {
      return false;
    }
  }

  if (filter.withIngredients.length > 0 || filter.withoutIngredients.length > 0) {
    // Ohne die Zutatenliste lässt sich nicht prüfen: dann bleibt das Rezept außen vor.
    if (ingredientNames === null) {
      return false;
    }
    const haystack = ingredientNames.map((name) => normalizeText(name)).join(' ');
    for (const wanted of filter.withIngredients) {
      if (!haystack.includes(normalizeText(wanted))) {
        return false;
      }
    }
    for (const unwanted of filter.withoutIngredients) {
      if (haystack.includes(normalizeText(unwanted))) {
        return false;
      }
    }
  }

  return true;
}

/** Sortiert die Rezepte. */
export function sortRecipes(
  recipes: readonly RecipeSummary[],
  sort: SortKey,
  hasQuery: boolean,
): RecipeSummary[] {
  const list = [...recipes];
  switch (sort) {
    case 'rating':
      return list.sort(
        (a, b) => (b.ratingAverage ?? -1) - (a.ratingAverage ?? -1) || a.title.localeCompare(b.title, 'de'),
      );
    case 'totalTime':
      return list.sort((a, b) => a.totalMinutes - b.totalMinutes || a.title.localeCompare(b.title, 'de'));
    case 'activeTime':
      return list.sort((a, b) => a.activeMinutes - b.activeMinutes || a.title.localeCompare(b.title, 'de'));
    case 'lastCooked':
      return list.sort((a, b) => (b.lastCookedAt ?? '').localeCompare(a.lastCookedAt ?? ''));
    case 'title':
      return list.sort((a, b) => a.title.localeCompare(b.title, 'de'));
    case 'relevance':
      // Ohne Suchtext verhält sich "Passung" wie "Neueste".
      if (hasQuery) {
        return list;
      }
      return list.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    case 'newest':
    default:
      return list.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }
}

/** Wendet Suchtext, Filter und Sortierung an. */
export function applyFilter(
  recipes: readonly RecipeSummary[],
  filter: RecipeFilter,
  index: MiniSearchLike | null,
  ingredientsById: ReadonlyMap<string, readonly string[]>,
): RecipeSummary[] {
  const matched = matchByQuery(recipes, filter.query, index);
  const needsIngredients = filter.withIngredients.length > 0 || filter.withoutIngredients.length > 0;
  const filtered = matched.filter((recipe) =>
    matchesFilter(recipe, filter, needsIngredients ? (ingredientsById.get(recipe.id) ?? []) : null),
  );
  return sortRecipes(filtered, filter.sort, filter.query.trim() !== '');
}
